<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const captainConfigStore = useCaptainConfigStore();
const { uiFlags } = storeToRefs(captainConfigStore);
const isLoading = computed(() => uiFlags.value.isFetching);

// ── Built-in prompts ──────────────────────────────────────────────────────
const BUILT_IN_PROMPTS = [
  {
    key: 'change_tone',
    titleKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.CHANGE_TONE',
    descKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.CHANGE_TONE_DESC',
    icon: 'i-lucide-audio-waveform',
  },
  {
    key: 'translate',
    titleKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.TRANSLATE',
    descKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.TRANSLATE_DESC',
    icon: 'i-lucide-languages',
  },
  {
    key: 'fix_spelling_grammar',
    titleKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.FIX_GRAMMAR',
    descKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.FIX_GRAMMAR_DESC',
    icon: 'i-lucide-spell-check',
  },
  {
    key: 'simplify_language',
    titleKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.SIMPLIFY',
    descKey: 'AI_PROMPTS_SETTINGS.BUILT_IN.SIMPLIFY_DESC',
    icon: 'i-lucide-align-left',
  },
];

const isPromptEnabled = key => {
  return captainConfigStore.getFeatures?.[key]?.enabled ?? true;
};

const togglePrompt = async (key, value) => {
  try {
    await captainConfigStore.updatePreferences({
      captain_features: { [key]: value },
    });
    useAlert(t('AI_PROMPTS_SETTINGS.SAVE_SUCCESS'));
  } catch {
    useAlert(t('AI_PROMPTS_SETTINGS.SAVE_ERROR'));
  }
};

// ── Custom prompts ────────────────────────────────────────────────────────
const customPrompts = ref([]);
const showAddForm = ref(false);
const newPromptName = ref('');
const newPromptText = ref('');

const cancelAdd = () => {
  showAddForm.value = false;
  newPromptName.value = '';
  newPromptText.value = '';
};

const addCustomPrompt = () => {
  if (!newPromptName.value.trim() || !newPromptText.value.trim()) return;
  customPrompts.value.push({
    id: Date.now(),
    name: newPromptName.value.trim(),
    prompt: newPromptText.value.trim(),
    enabled: true,
  });
  useAlert(t('AI_PROMPTS_SETTINGS.SAVE_SUCCESS'));
  cancelAdd();
};

const deleteCustomPrompt = id => {
  customPrompts.value = customPrompts.value.filter(p => p.id !== id);
};

onMounted(async () => {
  if (!Object.keys(captainConfigStore.getFeatures).length) {
    await captainConfigStore.fetch();
  }
});
</script>

<template>
  <div class="flex w-full max-w-none min-w-0 flex-col gap-6">
    <BaseSettingsHeader
      :title="t('AI_PROMPTS_SETTINGS.TITLE')"
      :description="t('AI_PROMPTS_SETTINGS.DESCRIPTION')"
    >
      <template #actions>
        <Button
          size="sm"
          icon="i-lucide-plus"
          :label="t('AI_PROMPTS_SETTINGS.ADD_BUTTON')"
          @click="showAddForm = true"
        />
      </template>
    </BaseSettingsHeader>

    <div class="flex w-full min-w-0 flex-col gap-4 px-8 md:px-10">
      <!-- Add custom prompt form -->
      <div
        v-if="showAddForm"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-5 flex flex-col gap-3 dark:border-n-slate-6"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('AI_PROMPTS_SETTINGS.CUSTOM.TITLE') }}
        </h3>
        <input
          v-model="newPromptName"
          class="w-full rounded-md border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-slate-6"
          :placeholder="t('AI_PROMPTS_SETTINGS.CUSTOM.NAME_PLACEHOLDER')"
        />
        <textarea
          v-model="newPromptText"
          rows="3"
          class="w-full rounded-md border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand resize-none dark:border-n-slate-6"
          :placeholder="t('AI_PROMPTS_SETTINGS.CUSTOM.PROMPT_PLACEHOLDER')"
        />
        <div class="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="faded"
            :label="t('AI_PROMPTS_SETTINGS.CUSTOM.CANCEL')"
            @click="cancelAdd"
          />
          <Button
            size="sm"
            :label="t('AI_PROMPTS_SETTINGS.CUSTOM.SAVE')"
            :disabled="!newPromptName.trim() || !newPromptText.trim()"
            @click="addCustomPrompt"
          />
        </div>
      </div>

      <!-- Built-in prompts -->
      <div
        class="rounded-lg border border-n-weak bg-n-solid-1 divide-y divide-n-weak dark:border-n-slate-6 dark:divide-n-slate-6 overflow-hidden"
      >
        <div
          v-for="prompt in BUILT_IN_PROMPTS"
          :key="prompt.key"
          class="flex items-center justify-between px-5 py-4 gap-4"
          :class="{ 'opacity-50': isLoading }"
        >
          <div class="flex items-start gap-3">
            <span
              class="size-5 text-n-slate-9 mt-0.5 shrink-0"
              :class="[prompt.icon]"
            />
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t(prompt.titleKey) }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t(prompt.descKey) }}
              </span>
            </div>
          </div>
          <Switch
            :model-value="isPromptEnabled(prompt.key)"
            :disabled="isLoading"
            @change="val => togglePrompt(prompt.key, val)"
          />
        </div>
      </div>

      <!-- Custom prompts -->
      <div
        v-if="customPrompts.length > 0"
        class="rounded-lg border border-n-weak bg-n-solid-1 divide-y divide-n-weak dark:border-n-slate-6 dark:divide-n-slate-6 overflow-hidden"
      >
        <div
          v-for="prompt in customPrompts"
          :key="prompt.id"
          class="flex items-center justify-between px-5 py-4 gap-4"
        >
          <div class="flex items-start gap-3 min-w-0">
            <span class="i-lucide-zap size-5 text-n-slate-9 mt-0.5 shrink-0" />
            <div class="flex flex-col gap-0.5 min-w-0">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ prompt.name }}
              </span>
              <span class="text-xs text-n-slate-11 truncate">
                {{ prompt.prompt }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <Switch v-model="prompt.enabled" />
            <button
              class="text-n-slate-9 hover:text-ruby-9 transition-colors"
              @click="deleteCustomPrompt(prompt.id)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
